const defaultNow = () => Date.now();

const toIsoTime = value => new Date(value).toISOString();

export const createFormRequestManager = ({
  now = defaultNow,
  log = details => console.info("[FormRequest]", details)
} = {}) => {
  let nextRequestId = 0;
  const activeRequests = new Map();

  const cancel = scope => {
    const activeRequest = activeRequests.get(scope);
    if (activeRequest) {
      activeRequest.controller.abort();
      activeRequests.delete(scope);
    }
  };

  const cancelAll = () => {
    [...activeRequests.keys()].forEach(cancel);
  };

  const run = async ({
    scope = "main",
    route,
    getCurrentRoute,
    isRouteCurrent,
    request,
    apply,
    setLoading,
    onError,
    isCanceled = error => error?.name === "AbortError"
  }) => {
    cancel(scope);

    const requestId = ++nextRequestId;
    const controller = new AbortController();
    const startTime = now();
    activeRequests.set(scope, { requestId, route, controller });

    setLoading?.(true);
    log({
      phase: "start",
      scope,
      requestId,
      requestedRoute: route,
      startTime: toIsoTime(startTime),
      completionTime: null,
      currentLocation: getCurrentRoute(),
      applied: false
    });

    let response;
    let requestError;

    try {
      response = await request({ signal: controller.signal, requestId });
    } catch (error) {
      requestError = error;
    }

    const completionTime = now();
    const currentLocation = getCurrentRoute();
    const latestRequest = activeRequests.get(scope);
    const isLatest = latestRequest?.requestId === requestId;
    const routeIsCurrent = isRouteCurrent
      ? isRouteCurrent()
      : currentLocation === route;
    const canceled = controller.signal.aborted || isCanceled(requestError);
    const applied = !requestError && !canceled && isLatest && routeIsCurrent;

    if (applied) {
      apply(response);
    } else if (requestError && !canceled && isLatest && routeIsCurrent) {
      onError?.(requestError);
    }

    log({
      phase: "complete",
      scope,
      requestId,
      requestedRoute: route,
      startTime: toIsoTime(startTime),
      completionTime: toIsoTime(completionTime),
      currentLocation,
      applied,
      canceled,
      durationMs: completionTime - startTime
    });

    if (isLatest) {
      activeRequests.delete(scope);
      if (routeIsCurrent) {
        setLoading?.(false);
      }
    }

    return { applied, canceled, requestId, response, error: requestError };
  };

  return { run, cancel, cancelAll };
};
